import { type FC, useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/components/base';
import { useCases } from '@/hooks/useCases';
import { useTasks } from '@/hooks/useTasks';
import { useCaseDeadlines } from '@/hooks/useCaseDeadlines';

import {
  buildPublicationAnalysisTaskInput,
  buildPublicationCaseDeadlineInput,
  findExistingPublicationDeadline,
} from '@/lib/publicationOperationalAction';
import { usePublications, type Publication, type PublicationStatus } from '@/hooks/usePublications';
import { fetchWithRetry } from '@/lib/apiRequest';
import PublicationMonitorsPanel from './components/PublicationMonitorsPanel';
import PublicationInboxWorkspace from './components/PublicationInboxWorkspace';
import PublicationMonitoringHealthPanel from './components/PublicationMonitoringHealthPanel';

type PublicationFormState = {
  provider: string;
  source: string;
  externalId: string;
  sourceName: string;
  tribunal: string;
  processNumber: string;
  title: string;
  content: string;
  summary: string;
  availableAt: string;
  publishedAt: string;
  capturedAt: string;
  lawyerOab: string;
  lawyerName: string;
  partyNames: string;
  rawJson: string;
  hash: string;
  status: PublicationStatus;
  notifiedAt: string;
  notificationChannel: string;
  notificationStatus: string;
  notificationError: string;
};

type DjennImportFormState = {
  processNumber: string;
  tribunal: string;
  startDate: string;
  endDate: string;
  lawyerOab: string;
  lawyerUf: string;
  lawyerName: string;
  caderno: string;
};

type DjennImportResult = {
  success?: boolean;
  totalFetched?: number;
  totalNormalized?: number;
  totalInserted?: number;
  totalSkippedAsDuplicate?: number;
  errors?: string[];
  error?: string;
};

const emptyForm: PublicationFormState = {
  provider: 'manual',
  source: '',
  externalId: '',
  sourceName: '',
  tribunal: '',
  processNumber: '',
  title: '',
  content: '',
  summary: '',
  availableAt: '',
  publishedAt: '',
  capturedAt: '',
  lawyerOab: '',
  lawyerName: '',
  partyNames: '',
  rawJson: '',
  hash: '',
  status: 'nova',
  notifiedAt: '',
  notificationChannel: '',
  notificationStatus: '',
  notificationError: '',
};

const emptyDiagnosticForm: DjennImportFormState = {
  processNumber: '',
  tribunal: '',
  startDate: '',
  endDate: '',
  lawyerOab: '',
  lawyerUf: '',
  lawyerName: '',
  caderno: '',
};

const statusOptions: Array<{ value: PublicationStatus; label: string }> = [
  { value: 'nova', label: 'Nova' },
  { value: 'lida', label: 'Lida' },
  { value: 'vinculada', label: 'Vinculada' },
  { value: 'prazo_sugerido', label: 'Prazo em conferência' },
  { value: 'tarefa_criada', label: 'Tarefa criada' },
  { value: 'arquivada', label: 'Arquivada' },
  { value: 'ignorada', label: 'Ignorada' },
];

const sourceOptions = [
  { value: 'manual', label: 'Manual' },
  { value: 'djen', label: 'DJEN/CNJ' },
  { value: 'datajud', label: 'DataJud' },
  { value: 'email', label: 'E-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'api_paga', label: 'API paga' },
  { value: 'outros', label: 'Outros' },
];

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

const formatShortDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const normalizeText = (value: string) => value.trim().toLowerCase();

const PublicationsPage: FC = () => {
  const { addToast } = useToast();
  const { publications, isLoadingPublications, isSavingPublication, publicationsError, loadPublications, createPublication, updatePublication, archivePublication } = usePublications();
  const { cases } = useCases();
  const { createTask } = useTasks({ autoLoad: false });

  const {
    createCaseDeadline,
    loadCaseDeadlines,
    isSavingCaseDeadline,
  } = useCaseDeadlines({
    autoLoad: false,
  });

  const [selectedId, setSelectedId] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PublicationFormState>(emptyForm);
  const [diagnosticForm, setDiagnosticForm] = useState<DjennImportFormState>(emptyDiagnosticForm);
  const [diagnosticResult, setDiagnosticResult] = useState<DjennImportResult | null>(null);
  const [diagnosticMessage, setDiagnosticMessage] = useState('');
  const [isRunningDiagnostic, setIsRunningDiagnostic] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    processNumber: '',
    caseId: '',
    clientSearch: '',
    date: '',
  });
  const [linkedCaseId, setLinkedCaseId] = useState('');

  const [
    isMatchingCases,
    setIsMatchingCases,
  ] = useState(false);

  useEffect(() => {
    if (!selectedId && publications.length > 0) {
      setSelectedId(publications[0].id);
    }
  }, [publications, selectedId]);

  const caseLookup = useMemo(() => new Map(cases.map((item) => [item.id, item])), [cases]);

  const publicationCards = useMemo(() => {
    const search = normalizeText(filters.search);
    const processNumber = normalizeText(filters.processNumber);
    const clientSearch = normalizeText(filters.clientSearch);
    const dateSearch = filters.date.trim();

    return publications.filter((publication) => {
      if (filters.status && publication.status !== filters.status) {
        return false;
      }

      if (processNumber && !normalizeText(publication.processNumber ?? '').includes(processNumber)) {
        return false;
      }

      if (filters.caseId && publication.linkedCaseId !== filters.caseId) {
        return false;
      }

      if (clientSearch) {
        const caseMatch = publication.linkedCaseId ? caseLookup.get(publication.linkedCaseId) : null;
        const haystack = [
          publication.sourceName,
          publication.tribunal,
          publication.processNumber,
          publication.title,
          publication.summary,
          publication.content,
          publication.partyNames,
          caseMatch?.clientName,
          caseMatch?.title,
        ]
          .filter(Boolean)
          .map((item) => normalizeText(String(item)))
          .join(' ');

        if (!haystack.includes(clientSearch)) {
          return false;
        }
      }

      if (search) {
        const haystack = [
          publication.source,
          publication.sourceName,
          publication.tribunal,
          publication.processNumber,
          publication.title,
          publication.summary,
          publication.content,
          publication.partyNames,
          publication.lawyerName,
          publication.lawyerOab,
          publication.matchType,
        ]
          .filter(Boolean)
          .map((item) => normalizeText(String(item)))
          .join(' ');

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (dateSearch) {
        const candidateDates = [publication.availableAt, publication.publishedAt, publication.capturedAt]
          .filter(Boolean)
          .map((value) => String(value).slice(0, 10));

        if (!candidateDates.some((value) => value === dateSearch)) {
          return false;
        }
      }

      return true;
    });
  }, [caseLookup, filters, publications]);

  const selectedPublication = useMemo(
    () => publicationCards.find((publication) => publication.id === selectedId) ?? publicationCards[0] ?? null,
    [publicationCards, selectedId]
  );

  useEffect(() => {
    if (!selectedPublication) {
      return;
    }

    setSelectedId(selectedPublication.id);
  }, [selectedPublication]);

  useEffect(
    () => {
      setLinkedCaseId(
        selectedPublication?.linkedCaseId ??
        ''
      );
    },
    [
      selectedPublication?.id,
      selectedPublication?.linkedCaseId,
    ]
  );

  const stats = useMemo(() => {
    const total = publications.length;
    const newCount = publications.filter((publication) => publication.status === 'nova').length;
    const linked = publications.filter((publication) => publication.status === 'vinculada').length;
    const archived = publications.filter((publication) => publication.status === 'arquivada' || publication.status === 'ignorada').length;

    return { total, newCount, linked, archived };
  }, [publications]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setLinkedCaseId('');
    setIsFormOpen(false);
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setLinkedCaseId('');
    setIsFormOpen(true);
  };

  const openEditForm = (publication: Publication) => {
    setEditingId(publication.id);
    setForm({
      provider: publication.provider,
      source: publication.source ?? '',
      externalId: publication.externalId ?? '',
      sourceName: publication.sourceName ?? '',
      tribunal: publication.tribunal ?? '',
      processNumber: publication.processNumber ?? '',
      title: publication.title,
      content: publication.content,
      summary: publication.summary ?? '',
      availableAt: publication.availableAt ?? '',
      publishedAt: publication.publishedAt ?? '',
      capturedAt: publication.capturedAt ?? '',
      lawyerOab: publication.lawyerOab ?? '',
      lawyerName: publication.lawyerName ?? '',
      partyNames: publication.partyNames ?? '',
      rawJson: publication.rawJson ?? '',
      hash: publication.hash ?? '',
      status: publication.status,
      notifiedAt: publication.notifiedAt ?? '',
      notificationChannel: publication.notificationChannel ?? '',
      notificationStatus: publication.notificationStatus ?? '',
      notificationError: publication.notificationError ?? '',
    });
    setLinkedCaseId(publication.linkedCaseId ?? '');
    setIsFormOpen(true);
  };

  const resetDiagnostic = () => {
    setDiagnosticForm(emptyDiagnosticForm);
    setDiagnosticResult(null);
    setDiagnosticMessage('');
  };

  const handleRunDiagnostic = async () => {
    setIsRunningDiagnostic(true);
    setDiagnosticMessage('');
    setDiagnosticResult(null);

    try {
      const response = await fetchWithRetry('/api/publications/djen/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          processNumber: diagnosticForm.processNumber,
          tribunal: diagnosticForm.tribunal,
          startDate: diagnosticForm.startDate,
          endDate: diagnosticForm.endDate,
          lawyerOab: diagnosticForm.lawyerOab,
          lawyerUf: diagnosticForm.lawyerUf,
          lawyerName: diagnosticForm.lawyerName,
          caderno: diagnosticForm.caderno,
        }),
      }, { timeoutMs: 20000, retries: 1, retryDelayMs: 400 });

      const data = (await response.json()) as DjennImportResult;

      if (!response.ok) {
        if (response.status === 401) {
          setDiagnosticMessage('Sessão expirada ou usuário não autenticado.');
          setDiagnosticResult(data);
          return;
        }


        setDiagnosticMessage(data?.error || 'Não foi possível consultar a fonte DJEN/CNJ no momento.');
        setDiagnosticResult(data);
        return;
      }

      setDiagnosticResult(data);

      if ((data.totalInserted ?? 0) > 0) {
        setDiagnosticMessage('Importação concluída.');
        addToast('Importação concluída.', 'success');
        await loadPublications();
        return;
      }

      if ((data.totalFetched ?? 0) > 0 && (data.totalSkippedAsDuplicate ?? 0) >= (data.totalNormalized ?? 0)) {
        setDiagnosticMessage('Nenhuma nova publicação inserida. Os registros encontrados já existiam.');
        addToast('Nenhuma nova publicação inserida.', 'info');
        await loadPublications();
        return;
      }

      setDiagnosticMessage('Importação concluída.');
      await loadPublications();
    } catch (error: any) {
      const message = String(error?.message ?? '').toLowerCase();
      if (message.includes('unauth') || message.includes('401')) {
        setDiagnosticMessage('Sessão expirada ou usuário não autenticado.');
      } else {
        setDiagnosticMessage('Não foi possível consultar a fonte DJEN/CNJ no momento.');
      }
    } finally {
      setIsRunningDiagnostic(false);
    }
  };

  const submitForm = async () => {
    const title = form.title.trim();
    const content = form.content.trim();
    const normalizedRawJson = form.rawJson.trim();

    if (!title) {
      addToast('Informe o título da publicação.', 'error');
      return;
    }

    const payload = {
      provider: form.provider.trim() || 'manual',
      source: form.source.trim() || null,
      externalId: form.externalId.trim() || null,
      sourceName: form.sourceName.trim() || null,
      tribunal: form.tribunal.trim() || null,
      processNumber: form.processNumber.trim() || null,
      title,
      content,
      summary: form.summary.trim() || null,
      availableAt: form.availableAt.trim() || null,
      publishedAt: form.publishedAt.trim() || null,
      capturedAt: form.capturedAt.trim() || null,
      lawyerOab: form.lawyerOab.trim() || null,
      lawyerName: form.lawyerName.trim() || null,
      partyNames: form.partyNames.trim() || null,
      rawJson: normalizedRawJson ? normalizedRawJson : null,
      hash: form.hash.trim() || null,
      status: form.status,
      notifiedAt: form.notifiedAt.trim() || null,
      notificationChannel: form.notificationChannel.trim() || null,
      notificationStatus: form.notificationStatus.trim() || null,
      notificationError: form.notificationError.trim() || null,
      linkedCaseId: linkedCaseId || null,
      linkedClientId: linkedCaseId ? caseLookup.get(linkedCaseId)?.clientId ?? null : null,
      matchType: linkedCaseId ? 'case' : null,
      confidence: linkedCaseId ? 0.92 : null,
      confirmedByUser: Boolean(linkedCaseId),
    };

    try {
      if (editingId) {
        await updatePublication(editingId, payload);
        addToast('Publicação atualizada.', 'success');
      } else {
        const created = await createPublication(payload);
        setSelectedId(created.id);
        addToast('Publicação cadastrada.', 'success');
      }
      resetForm();
      await loadPublications();
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar a publicação.', 'error');
    }
  };

  const handleLinkPublication = async () => {
    if (!selectedPublication || !linkedCaseId) {
      addToast('Selecione um caso para vincular.', 'error');
      return;
    }

    const selectedCase = caseLookup.get(linkedCaseId);
    if (!selectedCase) {
      addToast('Caso não encontrado.', 'error');
      return;
    }

    try {
      await updatePublication(selectedPublication.id, {
        status: 'vinculada',
        linkedCaseId: selectedCase.id,
        linkedClientId: selectedCase.clientId,
        matchType: 'manual',
        confidence: 1,
        confirmedByUser: true,
      });
      addToast('Publicação vinculada ao caso.', 'success');
      await loadPublications();
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível vincular a publicação.', 'error');
    }
  };

  const handleMarkAsRead = async () => {
    if (!selectedPublication) return;
    try {
      await updatePublication(selectedPublication.id, { status: 'lida' });
      addToast('Publicação marcada como lida.', 'success');
      await loadPublications();
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível marcar como lida.', 'error');
    }
  };

  const handleIgnore = async () => {
    if (!selectedPublication) return;
    try {
      await updatePublication(selectedPublication.id, { status: 'ignorada' });
      addToast('Publicação ignorada.', 'success');
      await loadPublications();
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível ignorar a publicação.', 'error');
    }
  };

  const handleArchive = async () => {
    if (!selectedPublication) return;
    try {
      await archivePublication(selectedPublication.id);
      addToast('Publicação arquivada.', 'success');
      await loadPublications();
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível arquivar a publicação.', 'error');
    }
  };

  const handleAutoMatchCases =
    async () => {
      setIsMatchingCases(
        true
      );

      try {
        const response =
          await fetchWithRetry(
            '/api/publications/auto-match',
            {
              method:
                'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify({}),
            }
          );

        const data:
          any =
          await response.json();

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
            'Não foi possível reprocessar os vínculos.'
          );
        }

        await loadPublications();

        const summary =
          data.summary ??
          {};

        addToast(
          `Matching concluído: ${summary.matched ?? 0} vínculo(s) CNJ exato(s), ${summary.ambiguous ?? 0} ambíguo(s), ${summary.unmatched ?? 0} sem caso correspondente.`,
          'success'
        );
      } catch (
        error:
          any
      ) {
        addToast(
          error?.message ||
          'Não foi possível executar o matching por CNJ.',
          'error'
        );
      } finally {
        setIsMatchingCases(
          false
        );
      }
    };

  const handleCreateTask = async () => {
    if (!selectedPublication) {
      return;
    }

    const selectedCase =
      selectedPublication.linkedCaseId
        ? caseLookup.get(
            selectedPublication.linkedCaseId
          ) ??
          null
        : null;

    try {
      await createTask(
        buildPublicationAnalysisTaskInput(
          selectedPublication,
          selectedCase
        )
      );

      await updatePublication(
        selectedPublication.id,
        {
          status:
            'tarefa_criada',
        }
      );

      addToast(
        'Tarefa de análise criada. Nenhum prazo processual foi presumido.',
        'success'
      );

      await loadPublications();
    } catch (error: any) {
      addToast(
        error?.message ||
          'Não foi possível criar a tarefa de análise.',
        'error'
      );
    }
  };

  const handleCreateSuggestedDeadline = async () => {
    if (!selectedPublication) {
      return;
    }

    if (
      !selectedPublication.linkedCaseId
    ) {
      addToast(
        'Vincule a publicação a um caso antes de registrar possível prazo.',
        'warning'
      );

      return;
    }

    const selectedCase =
      caseLookup.get(
        selectedPublication.linkedCaseId
      );

    if (!selectedCase) {
      addToast(
        'O caso vinculado não foi encontrado.',
        'error'
      );

      return;
    }

    try {
      const existingDeadlines =
        await loadCaseDeadlines({
          caseId:
            selectedCase.id,
        });

      const existing =
        findExistingPublicationDeadline(
          existingDeadlines,
          selectedPublication.id
        );

      if (existing) {
        if (
          selectedPublication.status !==
          'prazo_sugerido'
        ) {
          await updatePublication(
            selectedPublication.id,
            {
              status:
                'prazo_sugerido',
            }
          );
        }

        addToast(
          'Esta publicação já possui prazo operacional em conferência.',
          'info'
        );

        await loadPublications();

        return;
      }

      await createCaseDeadline(
        buildPublicationCaseDeadlineInput(
          selectedPublication,
          selectedCase
        )
      );

      await updatePublication(
        selectedPublication.id,
        {
          status:
            'prazo_sugerido',
        }
      );

      addToast(
        'Prazo operacional criado sem data final. Confira o marco inicial e a data no caso.',
        'success'
      );

      await loadPublications();
    } catch (error: any) {
      addToast(
        error?.message ||
          'Não foi possível registrar o prazo operacional.',
        'error'
      );
    }
  };

  const handleCopy = async () => {
    if (!selectedPublication) return;

    const text = selectedPublication.content || selectedPublication.summary || selectedPublication.title;
    try {
      await navigator.clipboard.writeText(text);
      addToast('Texto da publicação copiado.', 'success');
    } catch {
      addToast('Não foi possível copiar o texto.', 'error');
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground-900">Inbox Jurídica</h2>
          <p className="text-sm text-foreground-500 max-w-4xl">
            Central diária para identificar publicações, compreender o contexto jurídico, revisar inteligência assistida e acompanhar providências.
          </p>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            O módulo admite importação e monitoramento DJEN/CNJ quando a fonte estiver configurada. Toda publicação deve passar por triagem humana antes de gerar providências ou prazos operacionais.
          </div>
        </div>

        <PublicationMonitoringHealthPanel />

        <PublicationInboxWorkspace />

        <details className="rounded-2xl border border-background-200 bg-background-50">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-foreground-700">
            <span className="inline-flex items-center gap-2">
              <i className="ri-settings-3-line"></i>
              Administração e fluxo legado
            </span>
          </summary>

          <div className="space-y-6 border-t border-background-200 p-4">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
              Ferramentas administrativas e ações legadas permanecem preservadas durante a transição. O fluxo canônico de revisão e operacionalização será conectado na etapa LM5C.
            </div>

            <PublicationMonitorsPanel onRefreshInbox={() => void loadPublications()} />

        <div className="flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground-800">
              Vínculo automático com casos
            </p>

            <p className="mt-0.5 text-xs text-foreground-500">
              Usa exclusivamente número CNJ válido e correspondência exata. Nomes de partes ou clientes não geram vínculo automático.
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              void handleAutoMatchCases()
            }
            disabled={isMatchingCases}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-primary-200 px-3 py-2 text-xs font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i
              className={`ri-links-line ${
                isMatchingCases
                  ? 'animate-pulse'
                  : ''
              }`}
            ></i>

            {isMatchingCases
              ? 'Reprocessando...'
              : 'Reprocessar vínculos CNJ'}
          </button>
        </div>

        <details className="rounded-2xl border border-dashed border-primary-200 bg-primary-50/40 p-4">
          <summary className="cursor-pointer list-none text-sm font-semibold text-primary-800">
            Diagnóstico DJEN/CNJ
          </summary>

          <div className="mt-4 space-y-4">
            <p className="text-xs text-foreground-500">
              Ferramenta discreta para testar a importação manual controlada usando a sessão autenticada do sistema.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Processo</span>
                <input
                  value={diagnosticForm.processNumber}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, processNumber: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                  placeholder="0001234-56.2024.8.26.0100"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Tribunal</span>
                <input
                  value={diagnosticForm.tribunal}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, tribunal: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                  placeholder="TJSP, TRF3..."
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Caderno</span>
                <input
                  value={diagnosticForm.caderno}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, caderno: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                  placeholder="DJEN, diário, caderno..."
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data inicial</span>
                <input
                  type="date"
                  value={diagnosticForm.startDate}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, startDate: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data final</span>
                <input
                  type="date"
                  value={diagnosticForm.endDate}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, endDate: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">OAB</span>
                <input
                  value={diagnosticForm.lawyerOab}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, lawyerOab: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                  placeholder="SP123456"
                />
              </label>
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-foreground-600">
                        UF da OAB
                      </span>
                      <input
                        value={diagnosticForm.lawyerUf}
                        onChange={(event) =>
                          setDiagnosticForm((current) => ({
                            ...current,
                            lawyerUf: event.target.value.toUpperCase().slice(0, 2),
                          }))
                        }
                        maxLength={2}
                        className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                        placeholder="MG"
                      />
                    </label>
              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-foreground-600">Nome do advogado</span>
                <input
                  value={diagnosticForm.lawyerName}
                  onChange={(event) => setDiagnosticForm((current) => ({ ...current, lawyerName: event.target.value }))}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                  placeholder="Nome do advogado"
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void handleRunDiagnostic()}
                disabled={isRunningDiagnostic}
                className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60"
              >
                {isRunningDiagnostic ? 'Testando...' : 'Testar importação DJEN'}
              </button>
              <button
                type="button"
                onClick={resetDiagnostic}
                className="rounded-lg border border-background-200 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                Limpar
              </button>
            </div>

            {diagnosticMessage && (
              <div className="rounded-xl border border-background-200 bg-white px-4 py-3 text-sm text-foreground-700">
                {diagnosticMessage}
              </div>
            )}

            {diagnosticResult && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-xl border border-background-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-foreground-400">Total consultado</p>
                  <p className="mt-1 text-lg font-bold text-foreground-900">{diagnosticResult.totalFetched ?? 0}</p>
                </div>
                <div className="rounded-xl border border-background-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-foreground-400">Normalizadas</p>
                  <p className="mt-1 text-lg font-bold text-foreground-900">{diagnosticResult.totalNormalized ?? 0}</p>
                </div>
                <div className="rounded-xl border border-background-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-foreground-400">Inseridas</p>
                  <p className="mt-1 text-lg font-bold text-foreground-900">{diagnosticResult.totalInserted ?? 0}</p>
                </div>
                <div className="rounded-xl border border-background-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-foreground-400">Duplicadas</p>
                  <p className="mt-1 text-lg font-bold text-foreground-900">{diagnosticResult.totalSkippedAsDuplicate ?? 0}</p>
                </div>
                <div className="rounded-xl border border-background-200 bg-white p-3">
                  <p className="text-[10px] uppercase tracking-wider text-foreground-400">Erros</p>
                  <p className="mt-1 text-lg font-bold text-foreground-900">{diagnosticResult.errors?.length ?? 0}</p>
                </div>
              </div>
            )}

            {diagnosticResult?.errors && diagnosticResult.errors.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold">Avisos da importação</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {diagnosticResult.errors.map((error, index) => (
                    <li key={`${error}-${index}`}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </details>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-4">
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">Total</p>
            <p className="mt-1 text-xl font-bold text-foreground-900">{stats.total}</p>
          </div>
          <div className="rounded-2xl border border-background-200 bg-background-50 p-4">
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">Novas</p>
            <p className="mt-1 text-xl font-bold text-foreground-900">{stats.newCount}</p>
          </div>
          <div className="rounded-2xl border border-background-200 bg-background-50 p-4">
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">Vinculadas</p>
            <p className="mt-1 text-xl font-bold text-foreground-900">{stats.linked}</p>
          </div>
          <div className="rounded-2xl border border-background-200 bg-background-50 p-4">
            <p className="text-[10px] uppercase tracking-wider text-foreground-400">Arquivadas/ignoradas</p>
            <p className="mt-1 text-xl font-bold text-foreground-900">{stats.archived}</p>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openCreateForm}
              className="rounded-lg bg-primary-500 px-3 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              Cadastrar publicação
            </button>
            <button
              type="button"
              onClick={() => void loadPublications()}
              className="rounded-lg border border-background-200 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              Atualizar inbox
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Busca</span>
              <input
                value={filters.search}
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                placeholder="Título, resumo, conteúdo, órgão..."
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Status</span>
              <select
                value={filters.status}
                onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Processo</span>
              <input
                value={filters.processNumber}
                onChange={(event) => setFilters((current) => ({ ...current, processNumber: event.target.value }))}
                className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                placeholder="0012345-12.2026"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Caso</span>
              <select
                value={filters.caseId}
                onChange={(event) => setFilters((current) => ({ ...current, caseId: event.target.value }))}
                className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                {cases.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title} — {item.clientName ?? 'Cliente'}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Data</span>
              <input
                type="date"
                value={filters.date}
                onChange={(event) => setFilters((current) => ({ ...current, date: event.target.value }))}
                className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="text-xs font-medium text-foreground-600">Cliente / caso</span>
              <input
                value={filters.clientSearch}
                onChange={(event) => setFilters((current) => ({ ...current, clientSearch: event.target.value }))}
                className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                placeholder="Nome do cliente, caso, parte ou órgão"
              />
            </label>
            <div className="flex items-end justify-start gap-2">
              <button
                type="button"
                onClick={() => setFilters({ search: '', status: '', processNumber: '', caseId: '', clientSearch: '', date: '' })}
                className="rounded-lg border border-background-200 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        </div>

        {(isLoadingPublications || isSavingPublication) && (
          <div className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-600">
            {isLoadingPublications ? 'Carregando publicações reais...' : 'Salvando publicação...'}
          </div>
        )}

        {publicationsError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {publicationsError}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-3">
            {publicationCards.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-background-200 bg-background-50 p-8 text-center text-sm text-foreground-500">
                Nenhuma publicação encontrada. Cadastre uma publicação manualmente ou refine os filtros.
              </div>
            ) : (
              publicationCards.map((publication) => {
                const selected = publication.id === selectedPublication?.id;
                const caseMatch = publication.linkedCaseId ? caseLookup.get(publication.linkedCaseId) : null;

                return (
                  <button
                    key={publication.id}
                    type="button"
                    onClick={() => setSelectedId(publication.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition-colors ${
                      selected ? 'border-primary-300 bg-primary-50' : 'border-background-200 bg-background-50 hover:bg-background-100'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-semibold text-foreground-900">{publication.title}</h3>
                          <span className="rounded-full bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-600">
                            {statusOptions.find((option) => option.value === publication.status)?.label ?? publication.status}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500">
                          {publication.sourceName || publication.source || 'Fonte não informada'} · Processo {publication.processNumber || '-'} · Capturada em {formatDate(publication.capturedAt)}
                        </p>
                      </div>
                      <p className="text-xs text-foreground-500">{publication.tribunal || 'Tribunal não informado'}</p>
                    </div>

                    <p className="mt-3 line-clamp-3 text-sm text-foreground-600">
                      {publication.summary || publication.content || 'Sem conteúdo disponível.'}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-foreground-500">
                      <span className="rounded-full border border-background-200 px-2 py-1">Disponível: {formatShortDate(publication.availableAt)}</span>
                      <span className="rounded-full border border-background-200 px-2 py-1">Publicada: {formatShortDate(publication.publishedAt)}</span>
                      <span className="rounded-full border border-background-200 px-2 py-1">Cliente: {caseMatch?.clientName ?? publication.linkedClientId ?? 'A identificar'}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-900">Detalhe da publicação</h3>
                  <p className="text-xs text-foreground-500">Abrir, copiar, vincular e gerar ações.</p>
                </div>
                {selectedPublication && (
                  <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                    {statusOptions.find((option) => option.value === selectedPublication.status)?.label ?? selectedPublication.status}
                  </span>
                )}
              </div>

              {!selectedPublication ? (
                <p className="text-sm text-foreground-500">Selecione uma publicação na lista para ver os detalhes.</p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{selectedPublication.title}</p>
                    <p className="text-xs text-foreground-500">{selectedPublication.tribunal || 'Tribunal não informado'} · {selectedPublication.processNumber || 'Processo não informado'}</p>
                  </div>

                  <div className="rounded-xl border border-background-200 bg-white p-3 text-sm text-foreground-700">
                    <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">Conteúdo</p>
                    <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap text-sm leading-6">{selectedPublication.content || selectedPublication.summary || 'Sem conteúdo.'}</pre>
                  </div>

                  <div className="grid grid-cols-1 gap-2 text-xs text-foreground-600">
                    <div className="rounded-lg border border-background-200 bg-white p-2">Origem: {selectedPublication.sourceName || selectedPublication.source || '-'}</div>
                    <div className="rounded-lg border border-background-200 bg-white p-2">Disponibilização: {formatDate(selectedPublication.availableAt)}</div>
                    <div className="rounded-lg border border-background-200 bg-white p-2">Publicação: {formatDate(selectedPublication.publishedAt)}</div>
                    <div className="rounded-lg border border-background-200 bg-white p-2">Captura: {formatDate(selectedPublication.capturedAt)}</div>
                    <div className="rounded-lg border border-background-200 bg-white p-2">Responsável/OAB: {selectedPublication.lawyerName || '-'} {selectedPublication.lawyerOab ? `· ${selectedPublication.lawyerOab}` : ''}</div>
                    <div className="rounded-lg border border-background-200 bg-white p-2">Partes: {selectedPublication.partyNames || '-'}</div>
                    <div className="rounded-lg border border-background-200 bg-white p-2">Notificação: {selectedPublication.notificationStatus || '-'}{selectedPublication.notificationError ? ` · ${selectedPublication.notificationError}` : ''}</div>
                  </div>

                  {selectedPublication.matchType ===
                    'cnj_exact' &&
                    selectedPublication.linkedCaseId && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                        <div className="flex items-center gap-2 font-semibold">
                          <i className="ri-checkbox-circle-line"></i>
                          Vínculo encontrado por CNJ exato
                        </div>

                        <p className="mt-1">
                          Confiança técnica: 100%. O número CNJ válido desta publicação corresponde a um único caso cadastrado.
                        </p>

                        {!selectedPublication.confirmedByUser && (
                          <p className="mt-1 text-emerald-700">
                            O vínculo foi criado automaticamente. Você pode confirmá-lo pelo botão Vincular ou selecionar outro caso.
                          </p>
                        )}
                      </div>
                    )}

                  <label className="space-y-1 block">
                    <span className="text-xs font-medium text-foreground-600">Vincular a caso existente</span>
                    <select
                      value={linkedCaseId}
                      onChange={(event) => setLinkedCaseId(event.target.value)}
                      className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Selecione um caso</option>
                      {cases.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title} — {item.clientName ?? 'Cliente'}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={handleLinkPublication} className="rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600">
                      Vincular
                    </button>
                    <button type="button" onClick={handleMarkAsRead} className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100">
                      Marcar como lida
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateSuggestedDeadline}
                      disabled={
                        !selectedPublication.linkedCaseId ||
                        isSavingCaseDeadline ||
                        isSavingPublication
                      }
                      title={
                        selectedPublication.linkedCaseId
                          ? 'Registrar possível prazo no caso para conferência'
                          : 'Vincule primeiro esta publicação a um caso'
                      }
                      className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Criar prazo operacional
                    </button>

                    <button
                      type="button"
                      onClick={handleCreateTask}
                      disabled={isSavingPublication}
                      className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Criar tarefa de análise
                    </button>
                    <button type="button" onClick={handleIgnore} className="rounded-lg border border-amber-200 px-3 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50">
                      Ignorar
                    </button>
                    <button type="button" onClick={handleArchive} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50">
                      Arquivar
                    </button>
                    <button type="button" onClick={handleCopy} className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100">
                      Copiar texto
                    </button>
                    <button type="button" onClick={() => openEditForm(selectedPublication)} className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100">
                      Editar
                    </button>
                  </div>

                  <div className="rounded-xl border border-dashed border-background-200 bg-background-50 p-3 text-xs text-foreground-500">
                    Ao criar um prazo operacional, o sistema registra a origem da publicação, mas não presume termo inicial nem data final. A conferência jurídica é realizada dentro do caso.
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>

        {isFormOpen && (
          <div className="rounded-2xl border border-background-200 bg-background-50 p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-foreground-900">{editingId ? 'Editar publicação' : 'Cadastro manual de publicação'}</h3>
                <p className="text-xs text-foreground-500">Cole o texto integral da publicação e preencha os campos técnicos básicos.</p>
              </div>
              <button type="button" onClick={resetForm} className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100">
                Fechar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Origem</span>
                <select value={form.provider} onChange={(event) => setForm((current) => ({ ...current, provider: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm">
                  {sourceOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Fonte / sistema</span>
                <input value={form.source} onChange={(event) => setForm((current) => ({ ...current, source: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="TJSP, DJEN, Diário..." />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Órgão / Tribunal</span>
                <input value={form.tribunal} onChange={(event) => setForm((current) => ({ ...current, tribunal: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="TJSP, TRF3..." />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Número do processo</span>
                <input value={form.processNumber} onChange={(event) => setForm((current) => ({ ...current, processNumber: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="0000000-00.0000.0.00.0000" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Título</span>
                <input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Intimação - manifestação em 5 dias" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Status</span>
                <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as PublicationStatus }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm">
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 md:col-span-3">
                <span className="text-xs font-medium text-foreground-600">Conteúdo integral</span>
                <textarea value={form.content} onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))} className="w-full min-h-40 rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Cole aqui o texto integral da publicação..." />
              </label>
              <label className="space-y-1 md:col-span-3">
                <span className="text-xs font-medium text-foreground-600">Resumo</span>
                <textarea value={form.summary} onChange={(event) => setForm((current) => ({ ...current, summary: event.target.value }))} className="w-full min-h-24 rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Resumo curto para exibição na inbox." />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data disponível</span>
                <input type="date" value={form.availableAt.slice(0, 10)} onChange={(event) => setForm((current) => ({ ...current, availableAt: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data de publicação</span>
                <input type="date" value={form.publishedAt.slice(0, 10)} onChange={(event) => setForm((current) => ({ ...current, publishedAt: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data de captura</span>
                <input type="datetime-local" value={form.capturedAt.slice(0, 16)} onChange={(event) => setForm((current) => ({ ...current, capturedAt: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Identificador externo</span>
                <input value={form.externalId} onChange={(event) => setForm((current) => ({ ...current, externalId: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="ID da fonte" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Advogado responsável</span>
                <input value={form.lawyerName} onChange={(event) => setForm((current) => ({ ...current, lawyerName: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Nome" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">OAB</span>
                <input value={form.lawyerOab} onChange={(event) => setForm((current) => ({ ...current, lawyerOab: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="OAB/UF" />
              </label>
              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-foreground-600">Partes / nomes relevantes</span>
                <input value={form.partyNames} onChange={(event) => setForm((current) => ({ ...current, partyNames: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Cliente, parte adversa, órgão..." />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">JSON bruto</span>
                <textarea value={form.rawJson} onChange={(event) => setForm((current) => ({ ...current, rawJson: event.target.value }))} className="w-full min-h-24 rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Cole o JSON da fonte, se houver." />
              </label>
              <div className="grid grid-cols-1 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Channel de notificação</span>
                  <input value={form.notificationChannel} onChange={(event) => setForm((current) => ({ ...current, notificationChannel: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="email, whatsapp, interno..." />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Status da notificação</span>
                  <input value={form.notificationStatus} onChange={(event) => setForm((current) => ({ ...current, notificationStatus: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="pending, sent, error..." />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Erro da notificação</span>
                  <input value={form.notificationError} onChange={(event) => setForm((current) => ({ ...current, notificationError: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Mensagem de erro, se houver" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-foreground-600">Hash</span>
                  <input value={form.hash} onChange={(event) => setForm((current) => ({ ...current, hash: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Opcional" />
                </label>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={submitForm}
                disabled={isSavingPublication}
                className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60"
              >
                {editingId ? 'Salvar alterações' : 'Cadastrar publicação'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-background-200 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                Cancelar
              </button>
              <label className="inline-flex items-center gap-2 text-sm text-foreground-700">
                <span className="text-xs font-medium text-foreground-600">Vincular a caso</span>
                <select value={linkedCaseId} onChange={(event) => setLinkedCaseId(event.target.value)} className="rounded-lg border border-background-200 bg-white px-3 py-2 text-sm">
                  <option value="">Selecione</option>
                  {cases.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title} — {item.clientName ?? 'Cliente'}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-foreground-500">
                O cliente sugerido será derivado do caso vinculado quando existir.
              </p>
            </div>
          </div>
        )}
          </div>
        </details>
      </div>
    </AppLayout>
  );
};

export default PublicationsPage;
